import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaCalendarAlt, FaUserMd } from 'react-icons/fa';
import './Healthcare.css';

const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
const isoToday = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };

const DoctorSchedules = () => {
  const { t } = useTranslation();
  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState('');
  const [date, setDate] = useState(isoToday());
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem('access_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get('/api/healthcare/doctors', { headers }).then((res) => {
      const list = res.data?.doctors || [];
      setDoctors(list);
      if (list[0]) setDoctorId(list[0].id);
    }).catch(() => {});
  }, [headers]);

  const fetchSchedule = useCallback(async () => {
    if (!doctorId || !date) return;
    setLoading(true);
    try {
      const res = await axios.get(`/api/healthcare/doctors/${doctorId}/schedule`, { headers, params: { date } });
      setSchedule(res.data);
    } catch (err) {
      console.error('Failed to load schedule:', err);
      setSchedule(null);
    } finally {
      setLoading(false);
    }
  }, [headers, doctorId, date]);

  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

  const bookedByTime = useMemo(() => {
    const map = {};
    (schedule?.booked || []).forEach((b) => { map[b.time] = b; });
    return map;
  }, [schedule]);

  return (
    <div className="hc-page">
      <header className="hc-header">
        <div className="hc-header-title">
          <FaCalendarAlt className="hc-header-icon" />
          <div>
            <h1>{t('schedules.title', 'Schedules')}</h1>
            <p>{t('schedules.subtitle', 'View each practitioner availability and booked appointments.')}</p>
          </div>
        </div>
      </header>

      <div className="hc-controls">
        <select className="hc-input" value={doctorId} onChange={(e) => setDoctorId(e.target.value)}>
          {doctors.map((d) => <option key={d.id} value={d.id}>Dr {d.name}{d.speciality ? ` · ${d.speciality}` : ''}</option>)}
        </select>
        <input type="date" className="hc-input" value={date} onChange={(e) => setDate(e.target.value)} />
      </div>

      {loading ? (
        <div className="hc-state">{t('schedules.loading', 'Loading...')}</div>
      ) : !schedule ? (
        <div className="hc-state">{t('schedules.none', 'Select a doctor and date.')}</div>
      ) : (
        <div className="hc-slots-card">
          <div className="hc-legend">
            <span><i className="hc-dot available" /> {t('schedules.available', 'Available')}</span>
            <span><i className="hc-dot booked" /> {t('schedules.booked', 'Booked')}</span>
          </div>
          <div className="hc-slots">
            {schedule.slots.map((s) => {
              const b = bookedByTime[s.time];
              return (
                <div key={s.time} className={`hc-slot${s.available ? ' available' : ' booked'}`} title={b ? `${b.patient_name || ''}${b.reason ? ` — ${b.reason}` : ''}` : ''}>
                  <span className="hc-slot-time">{s.time}</span>
                  {b && <span className="hc-slot-name">{b.patient_name || t('schedules.reserved', 'Reserved')}</span>}
                </div>
              );
            })}
          </div>
          {schedule.slots.length === 0 && <div className="hc-state">{t('schedules.noSlots', 'No working hours configured for this doctor.')}</div>}
        </div>
      )}
    </div>
  );
};

export default DoctorSchedules;
